import { AG_GRID_LOCALE_DE } from "@ag-grid-community/locale";
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { AgGridAngular } from "ag-grid-angular";
import { MenuItem } from "@openng/optimus-ui/api";
import { ContextMenu } from "@openng/optimus-ui/contextmenu";
import {
    AgColumn,
    CellClickedEvent,
    CellContextMenuEvent,
    CellEditingStartedEvent,
    CellEditingStoppedEvent,
    CellEditorSelectorResult,
    CellFocusedEvent,
    CellKeyDownEvent,
    CellPosition,
    CellRendererSelectorResult,
    CellValueChangedEvent,
    ColDef,
    ColGroupDef,
    ColSpanParams,
    Column,
    ColumnMovedEvent,
    ColumnResizedEvent,
    ColumnState,
    ColumnVisibleEvent,
    FilterChangedEvent,
    FilterModel,
    FullWidthCellKeyDownEvent,
    GetRowIdFunc,
    GetRowIdParams,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellEditorParams,
    ICellRendererParams,
    IRowDragItem,
    IRowNode,
    ModelUpdatedEvent,
    RowDragEndEvent,
    RowDragMoveEvent,
    RowGroupOpenedEvent,
    RowHeightParams,
    SELECTION_COLUMN_ID,
    SortChangedEvent,
} from "ag-grid-community";
import { Config } from "src/modules/app-template/models/config.model";
import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { ExcelColumn } from "src/modules/enaio/netgen/ExcelColumn";
import { GuiUtils, GuiUtilsInternal } from "src/modules/utils/misc/gui-utils";
import { Arrays } from "src/modules/utils/shared/arrays";
import { Dates } from "src/modules/utils/shared/dates";
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from "src/modules/utils/shared/ordinary-object.model";
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from "src/modules/utils/shared/utils";
import { ObjectActionWithObject } from "../../models/object-action-with-object.model";
import { ObjectActionRegistry } from "../../models/object-action-registry.model";
import { RestEndpoint } from "../../models/rest-endpoint.model";
import { ToolTipDefinition } from "../../models/ToolTipDefinition.model";
import { SmTableColumnStatus } from "../../models/sm-table-column-status.model";
import { SmTableStatus } from "../../models/sm-table-status.model";
import { SmTableColumnWidthMode } from "../../models/sm-table-column-width-mode.model";
import { TableCellButton } from "../../models/table-cell-button.model";
import { TableCellEvent } from "../../models/table-cell-event.model";
import { TableCell } from "../../models/table-cell.model";
import { TableData } from "../../models/table-data.model";
import { TableRowEvent } from "../../models/table-row-event.model";
import { TableRow } from "../../models/table-row.model";
import { TableSizer } from "../../models/table-sizer.model";
import { FrontendField } from "../../netgen/FrontendField";
import { FrontendFieldType } from "../../netgen/FrontendFieldType";
import { FrontendForm } from "../../netgen/FrontendForm";
import { TableCellType } from "../../netgen/TableCellType";
import { TableColumn } from "../../netgen/TableColumn";
import { SmTableCellEditorComponent } from "../sm-table-cell-editor/sm-table-cell-editor.component";
import { SmTableCellComponent } from "../sm-table-cell/sm-table-cell.component";
import { SmTableHeaderComponent } from "../sm-table-header/sm-table-header.component";
import { SmTableTextFloatingFilterComponent } from "../sm-table-text-floating-filter/sm-table-text-floating-filter.component";
import { FrontendFieldListItem } from "../../netgen/FrontendFieldListItem";

@Component({
    selector: "sm-table",
    templateUrl: "./sm-table.component.html",
    styleUrl: "./sm-table.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class SmTableComponent {
    static defaultUseTableMenu = true;
    static defaultUseColumnMenu = true;
    static defaultActionButtons = false;
    static defaultActionButtonsOnRightClick = false;
    static defaultFilterButtons = true;

    static COLUMN_ID_REMOVE = "___???specialDelete???___";
    static COLUMN_ID_EDIT_ROW_FULL_SCREEN =
        "___???specialEditRowFullScreen???___";

    static locale: any = AG_GRID_LOCALE_DE;

    static setLanguage(lng: string): void {
        if (lng == "de") {
            AG_GRID_LOCALE_DE.noRowsToShow = "Keine Daten vorhanden";
        }
        SmTableComponent.locale = lng == "de" ? AG_GRID_LOCALE_DE : {};
    }

    _SmTableComponent = SmTableComponent;

    app = inject(MainAppService);

    @Input()
    compact: boolean = null;

    //Spaltenbreiten

    @Input()
    useTableSizer = true;
    @Input()
    columnWidthMode: SmTableColumnWidthMode | string =
        SmTableColumnWidthMode.Default;
    @Input()
    contentColumnMaxWidthRatio: number = null;
    @Input()
    horizontalScroll = false;
    @Input()
    suppressScrollOnNewData = false;

    private getColumnWidthMode(): SmTableColumnWidthMode {
        switch (this.columnWidthMode) {
            case SmTableColumnWidthMode.ContentFlex:
            case SmTableColumnWidthMode.Content:
            case SmTableColumnWidthMode.ContentNoExpand:
                return this.columnWidthMode as SmTableColumnWidthMode;
            default:
                return SmTableColumnWidthMode.Default;
        }
    }

    usesContentWidthsForFlexColumns(): boolean {
        return this.getColumnWidthMode() == SmTableColumnWidthMode.ContentFlex;
    }

    usesContentWidthsForAllColumns(): boolean {
        let mode = this.getColumnWidthMode();
        return mode == SmTableColumnWidthMode.Content ||
            mode == SmTableColumnWidthMode.ContentNoExpand;
    }

    expandsContentColumnsToTableWidth(): boolean {
        return this.getColumnWidthMode() == SmTableColumnWidthMode.Content;
    }

    constrainsContentColumnsToTableWidth(): boolean {
        return this.getColumnWidthMode() == SmTableColumnWidthMode.ContentNoExpand;
    }

    private getContentColumnMaxWidth(availableWidth: number): number {
        if (availableWidth <= 0 ||
            !(
                this.usesContentWidthsForFlexColumns() ||
                this.usesContentWidthsForAllColumns()
            ) ||
            this.contentColumnMaxWidthRatio == null) {
            return null;
        }
        let ratio = Math.min(1, Math.max(0, this.contentColumnMaxWidthRatio));
        return availableWidth * ratio;
    }

    //Filter

    @Input()
    allowFilter = true;

    _floatingFilter = false;
    quickFilterText = "";
    tableExpressionFilterText = "";
    tableExpressionFilterRows: Set<TableRow> = null;
    settingsItems: MenuItem[] = [];

    @Input()
    set floatingFilter(value: boolean) {
        this._floatingFilter = value;

        if (this.colDefs != null) {
            for (let col of this.colDefs) {
                col.floatingFilter = this._floatingFilter;
            }

            this.grid?.api?.setGridOption("columnDefs", this.colDefs);
        }
    }

    @Input()
    autoHeight = false;
    @Input()
    tabForSameCol = false;
    @Input()
    displayRowNumbers = false;
    @Input()
    displaySideBar = false;
    @Input()
    overrideUseTableCellRenderer: boolean = null;
    _allowAddRow = false;
    _allowRemoveRow = false;
    @Input()
    removeRowWarningMessage: string = null;
    @Input()
    handleRemove = false;
    @Input()
    allowCellTextSelection = true;
    _allowRowDrag = false;
    @Input()
    rowDragManaged = true;
    @Input()
    headerHeight: number = null;
    @Input()
    alowSorting = true;
    @Input()
    addRowOnTabInLastCell = false;
    _fullScreenRowEditor = false;
    _flashChanges = false;
    @Input()
    rowSelectionAllowed = true;
    @Input()
    canSelectRow: (row: TableRow) => boolean = null;
    @Input()
    multiSelection = false;
    @Input()
    groupSelectsDescendants = false;
    @Input()
    replaceSingleSelectionOnRowClick = false;
    @Input()
    editOnClick = true;
    @Input()
    stopEditingWhenCellsLoseFocus = false;
    @Input()
    disabled = false;
    _readOnly = false;
    _autoRowHeight = false;
    @Input()
    summaryRow: null | "numRows" | "numSel" | "numRowsNumSel" = null;
    @Input()
    headerAlign: "center" | "left" | "right" | undefined = null;
    @Input()
    isTree = false;
    @Input()
    treeGroupColumnId: string = null;
    @Input()
    groupDefaultExpanded = -1;
    @Input()
    invalidCellStyleClass = "xbg-red-100";
    @Input()
    additionalCellStyleClassCallback: (value: TableCellEvent) => string;
    _pinnedBottomRows: TableRow[];
    _checkBoxSelection = false;
    @Input()
    dropDraggedRow: (
        source: TableRow,
        dest: TableRow,
        checkOnly: boolean,
    ) => Partial<{
        allowDrop: boolean;
        dropText: string;
        styleClasses: string[];
    }>;
    @Input()
    getRowDragText: (params: IRowDragItem, dragItemCount: number) => string;
    @Input()
    performAddTableRow: () => void = null;
    @Input()
    performSetTableRows: (rows: TableRow[]) => void = null;
    @Input()
    mustRefresh: () => boolean = null;
    @Input()
    useTableMenu = SmTableComponent.defaultUseTableMenu;
    @Input()
    useColumnMenu = SmTableComponent.defaultUseColumnMenu;
    @Input()
    actionButtons = SmTableComponent.defaultActionButtons;
    @Input()
    actionButtonsOnRightClick =
        SmTableComponent.defaultActionButtonsOnRightClick;
    _data: TableData;
    //eigentlich richtigerweise: colDefs: (ColDef<any> | ColGroupDef<any>)[];
    colDefs: ColDef[];
    context: OrdinaryObject = {};
    internalPinnedBottomRows: any[] = [];
    ignoreDataChanges = false;
    settingBooleanHeaderCheckbox = false;
    applyingTableStatus = false;

    components: { [p: string]: any } = {
        agColumnHeader: SmTableHeaderComponent,
        smTableTextFloatingFilter: SmTableTextFloatingFilterComponent,
    };

    gridOptions: GridOptions = {
        tooltipShowMode: "whenTruncated",
        getChildCount:data => (data as TableRow).canHaveChildren ? 1 : 0,
        isExternalFilterPresent: () => this.tableExpressionFilterRows != null,
        doesExternalFilterPass: node => this.tableExpressionFilterRows?.has(node.data as TableRow) ?? true,
        suppressColumnMoveAnimation: true,
    };

    getDataPath = (data: any): string[] => data.path;
    isRowSelectable = (node: IRowNode<TableRow>): boolean => node.data != null && this.canSelectRow(node.data);
    autoGroupColumnDef: ColDef;

    isEnterprise = !Utils.isNoe(this.app.mainConfig.agGridEnterpriseKey);

    _selection: any;

    @Output()
    selectionChange = new EventEmitter<any>();
    @Output()
    onRowSelect = new EventEmitter<TableRow>();
    @Output()
    onRowRemoved = new EventEmitter<TableRowEvent>();
    @Output()
    onRowDoubleClicked = new EventEmitter<TableRow>();
    @Output()
    onCellClicked = new EventEmitter<TableCellEvent>();
    @Output()
    onCellValueChanged = new EventEmitter<TableCellEvent>();
    @Output()
    onCellEditingStarted = new EventEmitter<TableCellEvent>();
    @Output()
    onCellEditingStopped = new EventEmitter<TableCellEvent>();
    @Output()
    onCellFocused = new EventEmitter<TableCellEvent>();
    @Output()
    onFilterChanged = new EventEmitter<FilterChangedEvent>();
    @Output()
    onSortChanged = new EventEmitter<SortChangedEvent>();
    @Output()
    onTableStatusChanged = new EventEmitter<SmTableStatus>();
    @Output()
    onRowGroupOpened = new EventEmitter<RowGroupOpenedEvent<TableRow>>();
    @Output()
    onReady = new EventEmitter<SmTableComponent>();

    @ViewChild("grid") grid: AgGridAngular;
    @ViewChild("grid", { read: ElementRef })
    gridElement: ElementRef<HTMLElement>;
    @ViewChild("settingsMenu") settingsMenu: ContextMenu;
    @ViewChild("quickFilterInput") quickFilterInput: ElementRef<HTMLInputElement>;

    fullRowEditForm: FrontendForm;
    fullRowEditRowNode: IRowNode;

    tabToNextCellBound = this.tabToNextCell.bind(this);

    prefersDark = window.matchMedia("(prefers-color-scheme: dark)");

    @Input()
    set checkBoxSelection(value: boolean) {
        this._checkBoxSelection = value;
    }

    @Input()
    set pinnedBottomRows(value: TableRow[]) {
        this._pinnedBottomRows = value;
        this.updatePinnedBottomRows();
    }

    @Input()
    set flashChanges(value: boolean) {
        this._flashChanges = value;
        if (this.colDefs != null) {
            for (let [index, colDef] of this.colDefs.entries()) {
                colDef.enableCellChangeFlash = value && !this._data.columns[index].disableFlashOnChanges;
            }
        }
    }

    @Input()
    set fullScreenRowEditor(value: boolean) {
        this._fullScreenRowEditor = value;
        if (this.colDefs != null) {
            let indexOfCol = this.colDefs.findIndex(colDef => colDef.context.specialColumn == "editRowFullScreen");
            if (!this.isFullScreenRowEditorColumnVisible() && indexOfCol >= 0) {
                this.colDefs.splice(indexOfCol, 1);
            }
            if (this.isFullScreenRowEditorColumnVisible() && indexOfCol < 0) {
                this.colDefs = [
                    ...this.colDefs,
                    this.getFullScreenRowEditColDef(),
                ];
                this.updateColumnWidthsForMobile();
            }
        }
    }

    resizeObserver: ResizeObserver;

    refresh(force = true): void {
        if (this.grid?.api == null) {
            return;
        }
        this.grid.api.refreshCells({ force });
        if (this._data?.columns.some(col => col.headerCheckbox)) {
            this.grid.api.refreshHeader();
        }
    }

    replaceRows(rowsToRemove: TableRow[], rowsToAdd: TableRow[]): void {
        let removedRows = new Set(rowsToRemove);
        this._data.rows = [
            ...this._data.rows.filter(row => !removedRows.has(row)),
            ...rowsToAdd,
        ];
        this.grid.api.applyTransaction({ remove: rowsToRemove, add: rowsToAdd });
    }

    rowGroupOpened(event: RowGroupOpenedEvent<TableRow>): void {
        this.onRowGroupOpened.emit(event);
    }

    gridReady(_: GridReadyEvent): void {
        this.grid.api.setGridOption("suppressGroupChangesColumnVisibility", "suppressHideOnGroup");

        this.resizeObserver = new ResizeObserver(() => {
            if (this.usesContentWidthsForAllColumns()) {
                this.scheduleContentColumnWidths();
            }
            else if (!this.horizontalScroll &&
                this.getGridWidthForColumnFit() > 0) {
                this.grid.api.sizeColumnsToFit();
            }
        });
        this.resizeObserver.observe(this.gridElement.nativeElement);
        let bodyViewport =
            this.gridElement.nativeElement.querySelector(".ag-body-viewport");
        let centerViewport = this.gridElement.nativeElement.querySelector(".ag-center-cols-viewport");
        if (bodyViewport != null) {
            this.resizeObserver.observe(bodyViewport);
        }
        if (centerViewport != null) {
            this.resizeObserver.observe(centerViewport);
        }

        if (this.usesContentWidthsForAllColumns()) {
            this.scheduleContentColumnWidths();
        }

        this.onReady.emit(this);
    }

    updateColumnWidthsForMobile(): void {
        if (this.grid != null) {
            this.updateColumnWidthsForMobileInternal();
        }
        else {
            Utils.setTimerOnce(100, this.updateColumnWidthsForMobileInternal.bind(this));
        }
    }

    updateColumnWidthsForMobileInternal(): void {
        this.grid.api.setColumnsVisible(this._data.columns.slice(2).map(col => col.id), false);
    }

    sizeColumnsToFit(): boolean {
        if (this.usesContentWidthsForAllColumns()) {
            return this.applyContentColumnWidths();
        }
        if (this.grid?.api != null &&
            this.getGridWidthForColumnFit() > 0) {
            this.grid.api.sizeColumnsToFit();
            return true;
        }
        return false;
    }

    updateRemoveColumn(): void {
        if (this.colDefs != null) {
            let indexOfRemoveCol = this.colDefs.findIndex(colDef => colDef.context.specialColumn == "remove");
            if ((!this._allowRemoveRow || this._readOnly) &&
                indexOfRemoveCol >= 0) {
                this.colDefs.splice(indexOfRemoveCol, 1);
            }
            if (this._allowRemoveRow &&
                !this._readOnly &&
                indexOfRemoveCol < 0) {
                this.colDefs.push(this.getRemoveColDef());
            }
        }
    }

    @Input()
    set allowAddRow(value: boolean) {
        this._allowAddRow = value;
        this.updatePinnedBottomRows();
    }

    @Input()
    set allowRemoveRow(value: boolean) {
        this._allowRemoveRow = value;
        this.updateRemoveColumn();
    }

    @Input()
    set allowRowDrag(value: boolean) {
        this._allowRowDrag = value;
        if (this.colDefs != null) {
            let i = 0;
            for (let colDef of this.colDefs) {
                colDef.rowDrag =
                    this._allowRowDrag && i++ == 0
                        ?params => (params.data as TableRow).canDrag
                        : false;
            }
        }
    }

    @Input()
    set autoRowHeight(value: boolean) {
        this._autoRowHeight = value;
        if (this.colDefs != null) {
            for (let colDef of this.colDefs) {
                colDef.autoHeight = value || colDef.context?.tableColumn?.autoHeight;
            }
        }
    }

    @Input()
    set readOnly(value: boolean) {
        this._readOnly = value;
        this.updateRemoveColumn();
    }

    private getOneButtonColWidth(): Partial<ColDef> {
        let widthFlex = TableSizer.get(this.getTheme()).calculate(this._data, new TableColumn("", "", TableCellType.button, 1), false);
        return {
            width: widthFlex.width,
            flex: widthFlex.flex,
            minWidth: widthFlex.minWidth,
            maxWidth: widthFlex.maxWidth,
        };
    }

    private getRemoveColDef(): ColDef {
        return {
            colId: SmTableComponent.COLUMN_ID_REMOVE,
            context: {
                specialColumn: "remove",
                table: this,
                tableColumn: new TableColumn(SmTableComponent.COLUMN_ID_REMOVE, "", TableCellType.button, 1),
            },
            ...this.getOneButtonColWidth(),
            cellRenderer: SmTableCellComponent,
            cellRendererParams: {
                table: this,
            },
            valueGetter:params =>
                (params.data as TableRow).canDelete()
                    ? [new TableCellButton("remove", "fas fa-trash", "Löschen")]
                    : [],
        };
    }

    private getFullScreenRowEditColDef(): ColDef {
        return {
            colId: SmTableComponent.COLUMN_ID_EDIT_ROW_FULL_SCREEN,
            context: {
                specialColumn: "editRowFullScreen",
                table: this,
                tableColumn: new TableColumn(SmTableComponent.COLUMN_ID_EDIT_ROW_FULL_SCREEN, "", TableCellType.button, 1),
            },
            ...this.getOneButtonColWidth(),
            cellRenderer: SmTableCellComponent,
            cellRendererParams: {
                table: this,
            },
            pinned: "left",
            valueGetter:params =>
                params.node.rowPinned != "bottom"
                    ? [
                          new TableCellButton(
                              "editRow",
                              "fas fa-pencil",
                              "Zeile editieren",
                          ),
                      ]
                    : [],
        };
    }

    sortCache = new Map<any, string>();

    @Input()
    set data(value: TableData) {
        if (this.ignoreDataChanges) {
            return;
        }
        if (value?.rows !== this._data?.rows && this.tableExpressionFilterRows != null) {
            this.tableExpressionFilterText = "";
            this.tableExpressionFilterRows = null;
        }
        let columnsChanged = !Arrays.equalsFlat(value?.columns, this._data?.columns);
        this._data = value;

        this.context = {
            table: this,
        };
        if (this._data != null && columnsChanged) {
            if (this.usesContentWidthsForAllColumns()) {
                this.contentFitFillColumnWidthsApplied = false;
            }
            this.colDefs = [];
            let colIdx = 0;
            let theme = this.getTheme();
            for (let col of this._data.columns) {
                let sortIndex = this._data.sort?.findIndex(sc => sc.id == col.id);
                let widthFlex = this.useTableSizer
                    ? col.getRealWidth(
                          this._data,
                          this.usesContentWidthsForFlexColumns() ||
                              this.usesContentWidthsForAllColumns(),
                          theme,
                          this.usesContentWidthsForAllColumns(),
                      )
                    : null;
                let maxContentWidth = this.getContentColumnMaxWidth(this.getGridWidthForColumnFit());
                if (widthFlex != null &&
                    this.usesContentWidthsForFlexColumns() &&
                    maxContentWidth != null &&
                    widthFlex.flex != null) {
                    widthFlex.flex = Math.min(widthFlex.flex, maxContentWidth);
                }
                if (widthFlex != null &&
                    col.maxWidth != null &&
                    widthFlex.width > col.maxWidth) {
                    widthFlex.width = col.maxWidth;
                }
                //  console.log(col.id + " " + widthFlex?.width+ " " + widthFlex?.flex + " " + widthFlex?.minWidth + " " + widthFlex?.maxWidth);
                let colDef: ColDef = {
                    field: col.id,
                    colId: col.id,
                    cellDataType:
                        col.cellType == TableCellType.bool
                            ? "boolean"
                            : col.cellType == TableCellType.int ||
                                col.cellType == TableCellType.decimal
                              ? "number"
                              : col.cellType == TableCellType.date ||
                                  col.cellType == TableCellType.dateTime
                                ? "date"
                                : col.cellType == TableCellType.object
                                  ? "object"
                                  : "text",
                    valueGetter:params => {
                        if (params.node.group && (this.treeGroupColumnId == null || params.data == null)) {
                            return null;
                        }
                        let v = TableCell.getValue(params.data.values[col.id]);
                        if (col.id == "selected") {
                            console.log(
                                "ValueGetter for col " +
                                    col.id +
                                    " called for row " +
                                    params.node.rowIndex +
                                    ": " +
                                    v,
                            );
                        }

                        return col.getValue(v, () => {
                            let colx = this.getColDefAndIndex(col.id);
                            return new TableCellEvent(
                                v,
                                params.node.data as TableRow,
                                colx.def,
                                params.node.rowIndex,
                                colx.index,
                            );
                        });
                    },
                    valueSetter:params => {
                        let colx = this.getColDefAndIndex(col.id);
                        col.setValue(
                            new TableCellEvent(
                                params.newValue,
                                params.node.data as TableRow,
                                colx.def,
                                params.node.rowIndex,
                                colx.index,
                            ),
                        );
                        return true;
                    },
                    valueFormatter:params => {
                        try {
                            let colx = this.getColDefAndIndex(col.id);

                            if (col.suppressRepeatingValues) {
                                for (let i = params.node.rowIndex - 1;
                                    i >= 0;
                                    i--) {
                                    let displayedRowAbove =
                                        this.grid.api.getDisplayedRowAtIndex(i);
                                    let row =
                                        displayedRowAbove?.data as TableRow;
                                    if (row?.virtual) {
                                        continue;
                                    }
                                    if (row?.values[col.id] == params.value) {
                                        return "";
                                    }
                                    break;
                                }
                            }

                            return colx == null
                                ? params.value
                                : col.formatValue(
                                      new TableCellEvent(
                                          params.value,
                                          params.node.data as TableRow,
                                          colx.def,
                                          params.node.rowIndex,
                                          colx.index,
                                      ),
                                      this.app.language,
                                  );
                        }
                        catch (error: any) {
                            console.log(
                                "Der Wert " +
                                    Objects.toString(params.value) +
                                    " für die Spalte " +
                                    col.id +
                                    " konnte nicht formatiert werden. " +
                                    error.message,
                            );
                            return "<???>";
                        }
                    },
                    context: {
                        specialColumn: null,
                        table: this,
                        tableColumn: col,
                    },
                    contextMenuItems: this.isEnterprise
                        ?params => {
                              (params.event as any).preventDefault();
                              return [];
                          }
                        : null,
                    suppressMovable: true,
                    editable:params =>
                        this.isCellEditableInternal(params.node, col),
                    cellEditorSelector:params =>
                        this.getCellEditor(params, col),
                    cellEditorPopup: col.editorPopup,
                    //singleClickEdit: true,
                    filter: col.isFilterable()
                        ? this.isEnterprise
                            ? "agMultiColumnFilter"
                            : col.cellType == TableCellType.string
                            ? "agTextColumnFilter"
                            : true
                        : false,
                    filterParams: this.getFilterParams(col),
                    floatingFilterComponent: this.isEnterprise && col.cellType == TableCellType.string ? "smTableTextFloatingFilter" : undefined,
                    floatingFilter: this._floatingFilter && col.isFilterable(),
                    headerName: col.caption,
                    //wrapHeaderText: true,
                    autoHeaderHeight: true,
                    pinned: col.frozen,
                    cellClass:params => {
                        if (params.node.group && (this.treeGroupColumnId == null || params.data == null)) {
                            return null;
                        }
                        let alignClass =
                            col.align == "center"
                                ? "ag-center-aligned-cell "
                                : col.align == "right"
                                  ? "ag-right-aligned-cell "
                                  : "";
                        let styleClass: string;
                        if (params.data.values[col.id] instanceof TableCell) {
                            styleClass = (
                                params.data.values[col.id] as TableCell
                            ).styleClass;
                        }
                        if (styleClass == null && col.getStyleClassCallback != null &&
                            params.node.rowPinned != "bottom") {
                            let colx = this.getColDefAndIndex(col.id);
                            styleClass = col.getStyleClassCallback(
                                new TableCellEvent(
                                    TableCell.getValue(params.data.values[col.id]),
                                    params.node.data as TableRow,
                                    colx.def,
                                    params.node.rowIndex,
                                    colx.index,
                                ),
                            );
                        }
                        if (styleClass == null) {
                            styleClass = col.styleClass;
                        }
                        if (this.additionalCellStyleClassCallback != null &&
                            params.node.rowPinned != "bottom") {
                            let colx = this.getColDefAndIndex(col.id);
                            let additionalStyleClass = this.additionalCellStyleClassCallback(
                                new TableCellEvent(
                                    TableCell.getValue(params.data.values[col.id]),
                                    params.node.data as TableRow,
                                    colx.def,
                                    params.node.rowIndex,
                                    colx.index,
                                ),
                            );
                            if (!Utils.isNoe(additionalStyleClass)) {
                                styleClass = Utils.isNoe(styleClass) ? additionalStyleClass : styleClass + " " + additionalStyleClass;
                            }
                        }
                        return alignClass + (styleClass ?? "");
                    },
                    cellStyle:params => {
                        if (params.node.group && (this.treeGroupColumnId == null || params.data == null)) {
                            return null;
                        }
                        if (params.data.values[col.id] instanceof TableCell) {
                            let result = params.data.values[col.id].style;
                            if (result != null) {
                                return result;
                            }
                        }
                        return null;
                    },
                    cellRendererSelector:params =>
                        this.isTree && col.id == this.treeGroupColumnId
                            ? {
                                  component: "agGroupCellRenderer",
                                  params: { suppressCount: true },
                              }
                            : this.getCellRenderer(params, col),
                    cellRenderer: col.cellRenderer,
                    enableCellChangeFlash:
                        this._flashChanges && !col.disableFlashOnChanges,
                    rowDrag:
                        this._allowRowDrag && colIdx == 0
                            ?params => (params.data as TableRow).canDrag
                            : false,
                    enableRowGroup: this.isEnterprise,
                    enableValue: this.isEnterprise,
                    enablePivot: this.isEnterprise,
                    showRowGroup:
                        this.isTree && this.treeGroupColumnId != null
                            ? col.id == this.treeGroupColumnId
                                ? true
                                : null
                            : true,
                    sortable: col.isSortable(),
                    sort:
                        sortIndex == -1 || sortIndex == null
                            ? null
                            : this._data.sort[sortIndex].ascending
                              ? "asc"
                              : "desc",
                    sortIndex: sortIndex != -1 ? sortIndex : null,
                    comparator: (
                        col.useComparator == null
                            ? col.cellType == TableCellType.string
                            : col.useComparator
                    )
                        ? (a, b, cell1, cell2) => {
                              const getCmp = (v: any): string => {
                                  (window as any).cmpCount = ((window as any).cmpCount ?? 0) + 1;
                                  let cached = this.sortCache.get(v);
                                  if (cached == null) {
                                      (window as any).cmpCountCached = ((window as any).cmpCountCached ?? 0) + 1;
                                      cached = Objects.toString(v).normalize("NFD").toLowerCase();
                                      this.sortCache.set(v, cached);
                                  }
                                  return cached;
                              };

                              if (cell1?.data instanceof TableRow &&
                                  cell1.data.values[col.id] instanceof TableCell) {
                                  a = (cell1.data.values[col.id] as TableCell) .comparable ?? a;
                              }
                              if (cell2?.data instanceof TableRow &&
                                  cell2.data.values[col.id] instanceof TableCell) {
                                  b = (cell2.data.values[col.id] as TableCell) .comparable ?? b;
                              }

                              let cmp1 =
                                  col.cellType == TableCellType.string
                                      ? getCmp(a)
                                      : a;
                              let cmp2 =
                                  col.cellType == TableCellType.string
                                      ? getCmp(b)
                                      : b;

                              return cmp1 < cmp2 ? -1 : cmp1 > cmp2 ? 1 : 0;
                          }
                        : null,
                    colSpan:params => this.getCellColSpan(params, col),
                    tooltipValueGetter:params => {
                        if (params.data == null || params.node == null ||
                            params.node.rowPinned == "bottom") {
                            return null;
                        }
                        if (col.toolTipGetter != null) {
                            let colx = this.getColDefAndIndex(col.id);
                            return colx == null
                                ? null
                                : col.toolTipGetter(
                                      new TableCellEvent(
                                          TableCell.getValue(params.data.values[col.id]),
                                          params.node.data as TableRow,
                                          colx.def,
                                          params.node.rowIndex,
                                          colx.index,
                                      ),
                                  );
                        }
                        if (col.cellRenderer != null ||
                            this.isTree && col.id == this.treeGroupColumnId) {
                            return null;
                        }
                        let rendererParams = {
                            node: params.node,
                            data: params.data,
                            colDef,
                            value: params.value
                        };
                        if (this.getCellRenderer(rendererParams, col) != null) {
                            return null;
                        }
                        return params.valueFormatted ?? (typeof params.value == "string" ? params.value : null);
                    },
                    initialWidth: this.usesContentWidthsForAllColumns()
                        ? widthFlex?.width ?? widthFlex?.flex
                        : widthFlex?.width,
                    initialFlex: this.usesContentWidthsForAllColumns()
                        ? null
                        : widthFlex?.flex,
                    minWidth: widthFlex?.minWidth,
                    maxWidth: this.usesContentWidthsForAllColumns()
                        ? col.maxWidth
                        : widthFlex?.maxWidth,
                    resizable: col.resizable,
                    autoHeight: this._autoRowHeight || col.autoHeight,
                    wrapText: col.wrapText,
                };
                this.colDefs.push(colDef);
                colIdx++;
            }
            if (this._allowRemoveRow && !this._readOnly) {
                this.colDefs.push(this.getRemoveColDef());
            }
            if (this.isFullScreenRowEditorColumnVisible() &&
                this.app.isMobile()) {
                this.colDefs.push(this.getFullScreenRowEditColDef());
            }

            let delCols: number[] = [];
            let groupCaptions = Arrays.getUnique(this._data.columns.filter(col => !Utils.isNoe(col.groupCaption)).map(col => col.groupCaption));
            for (let groupCaption of groupCaptions) {
                let groupColumn = null;
                colIdx = 0;
                for (let col of this._data.columns) {
                    if (col.groupCaption == groupCaption) {
                        if (groupColumn == null) {
                            groupColumn = {
                                headerName: groupCaption,
                                children: [this.colDefs[colIdx]],
                            };
                            this.colDefs[colIdx] = groupColumn;
                        }
                        else {
                            groupColumn.children.push(this.colDefs[colIdx]);
                            delCols.push(colIdx);
                        }
                    }
                    colIdx++;
                }
            }

            for (let delCol of Arrays.reverse(Arrays.sort(delCols))) {
                this.colDefs.splice(delCol, 1);
            }

            if (this.isTree) {
                /*    this.autoGroupColumnDef = this.colDefs.splice(0, 1)[0];
                if (this.autoGroupColumnDef != null) {
                    this.autoGroupColumnDef.field = "values.key";
                }*/
                //PRAGMATIC Hardcodiert
                /* this.autoGroupColumnDef = {
                    headerName: "Eigenschaft",
                    field: "values.key",
                    cellRendererParams: {
                        suppressCount: true
                    },
                    contextMenuItems: this.isEnterprise ? params => {
                        (params.event as any).preventDefault();
                        return [];
                    } : null
                };*/
            }
            else {
                this.autoGroupColumnDef = null;
            }

            this.updatePinnedBottomRows();

            if (this.isFullScreenRowEditorColumnVisible()) {
                this.updateColumnWidthsForMobile();
            }
            if (this.usesContentWidthsForAllColumns()) {
                this.scheduleContentColumnWidths();
            }
            else if (!this.horizontalScroll) {
                GuiUtils.angularTimer(() => {
                    this.doSizeColumnsToFit = true;
                });
            }
        }
        else if (this._data != null &&
            this.usesContentWidthsForAllColumns()) {
            this.scheduleContentColumnWidths();
        }
    }

    private scheduleContentColumnWidths(): void {
        if (this.contentFitFillColumnWidthsScheduled) {
            return;
        }
        this.contentFitFillColumnWidthsScheduled = true;
        GuiUtils.angularTimer(() => {
            this.contentFitFillColumnWidthsScheduled = false;
            this.applyContentColumnWidths();
        });
    }

    private applyContentColumnWidths(): boolean {
        if (this.grid?.api == null ||
            this._data == null ||
            !this.useTableSizer) {
            return false;
        }

        let gridWidth = this.getGridWidthForColumnFit();
        if (gridWidth <= 0) {
            return false;
        }

        let tableColumnIds = new Set(this._data.columns.map(col => col.id));
        let displayedColumns = this.grid.api.getAllDisplayedColumns();
        let fixedWidth = 0;
        for (let displayedColumn of displayedColumns) {
            let colId = displayedColumn.getColId();
            if (!tableColumnIds.has(colId)) {
                fixedWidth += displayedColumn.getActualWidth();
            }
        }

        let availableWidth = Math.max(0, gridWidth - fixedWidth);
        let maxContentWidth = this.getContentColumnMaxWidth(availableWidth);
        let columnWidths = this._data.columns
            .filter(col =>
                displayedColumns.some(displayedColumn => displayedColumn.getColId() == col.id),
            )
            .map(col => {
                let widthFlex = col.getRealWidth(this._data, true, this.getTheme(), true);
                let idealWidth = widthFlex?.width ?? widthFlex?.flex ?? widthFlex?.maxWidth ?? widthFlex?.minWidth ?? 0;
                let minWidth = Math.max(widthFlex?.minWidth ?? 0, col.minWidth ?? 0);
                if (idealWidth < minWidth) {
                    idealWidth = minWidth;
                }
                if (maxContentWidth != null) {
                    idealWidth = Math.max(minWidth, Math.min(idealWidth, maxContentWidth));
                }
                return {
                    col,
                    idealWidth,
                    width: idealWidth,
                    fixed: false,
                };
            })
            .filter(item => item.idealWidth > 0);

        let sumIdealWidth = columnWidths.reduce((sum, item) => sum + item.idealWidth, 0);
        if (sumIdealWidth <= 0) {
            return false;
        }

        if (this.constrainsContentColumnsToTableWidth() &&
            sumIdealWidth > availableWidth) {
            let factor = availableWidth / sumIdealWidth;
            for (let item of columnWidths) {
                item.width = Math.max(1, item.idealWidth * factor);
            }
        }
        else if (this.expandsContentColumnsToTableWidth() &&
            sumIdealWidth < availableWidth) {
            let remainingWidth = availableWidth;
            let remainingIdealWidth = sumIdealWidth;
            while (remainingWidth > 0 && remainingIdealWidth > 0) {
                let factor = remainingWidth / remainingIdealWidth;
                let changed = false;
                for (let item of columnWidths.filter(item2 => !item2.fixed)) {
                    let width = item.idealWidth * factor;
                    if (item.col.maxWidth != null &&
                        width > item.col.maxWidth) {
                        item.width = item.col.maxWidth;
                        item.fixed = true;
                        remainingWidth -= item.width;
                        remainingIdealWidth -= item.idealWidth;
                        changed = true;
                    }
                }
                if (!changed || columnWidths.every(item => item.fixed)) {
                    for (let item of columnWidths.filter(item2 => !item2.fixed)) {
                        item.width = item.idealWidth * factor;
                    }
                    break;
                }
            }
        }

        let roundedWidths = columnWidths.map(item => ({
            item,
            width: Math.floor(item.width),
        }));
        let roundedWidthSum = roundedWidths.reduce((sum, item) => sum + item.width, 0);
        let remainingPixels = this.expandsContentColumnsToTableWidth()
            ? Math.max(0, Math.floor(availableWidth) - roundedWidthSum)
            : 0;
        for (let item of roundedWidths) {
            if (remainingPixels <= 0) {
                break;
            }
            if (item.item.col.maxWidth == null ||
                item.width < item.item.col.maxWidth) {
                item.width++;
                remainingPixels--;
            }
        }

        let result = this.grid.api.applyColumnState({
            state: roundedWidths.map(item => ({
                colId: item.item.col.id,
                flex: null,
                width: item.width,
            })),
        });
        let horizontalOverflow = Math.ceil(this.getContentColumnHorizontalOverflow());
        let adjustedForOverflow = false;
        for (let item of [...roundedWidths].sort((a, b) => b.width - a.width)) {
            if (horizontalOverflow <= 0) {
                break;
            }
            let reduction = Math.min(horizontalOverflow, Math.max(0, item.width - 1));
            item.width -= reduction;
            horizontalOverflow -= reduction;
            adjustedForOverflow ||= reduction > 0;
        }
        if (adjustedForOverflow) {
            result = this.grid.api.applyColumnState({
                state: roundedWidths.map(item => ({
                    colId: item.item.col.id,
                    flex: null,
                    width: item.width,
                })),
            });
        }
        this.contentFitFillColumnWidthsApplied = result;
        return result;
    }

    private getContentColumnHorizontalOverflow(): number {
        let centerViewport = this.gridElement?.nativeElement?.querySelector(".ag-center-cols-viewport");
        return Math.max(
            0,
            (centerViewport?.scrollWidth ?? 0) -
                (centerViewport?.clientWidth ?? 0),
        );
    }

    private getGridWidthForColumnFit(): number {
        let element = this.gridElement?.nativeElement;
        let gridBody = element?.querySelector(".ag-body");
        let bodyViewport = element?.querySelector(".ag-body-viewport");
        let centerViewport = element?.querySelector(".ag-center-cols-viewport");
        if ((gridBody?.clientWidth ?? 0) <= 0 ||
            (bodyViewport?.clientWidth ?? 0) <= 0 ||
            (centerViewport?.clientWidth ?? 0) <= 0) {
            return 0;
        }
        let verticalScroll = element?.querySelector(".ag-body-vertical-scroll");
        let verticalScrollbarVisible = (bodyViewport?.scrollHeight ?? 0) > (bodyViewport?.clientHeight ?? 0);
        let verticalScrollbarWidth = verticalScrollbarVisible
            ? verticalScroll?.clientWidth ?? 0
            : 0;
        let availableWidth = (gridBody?.clientWidth ?? bodyViewport?.clientWidth ?? element?.clientWidth ?? 0) - verticalScrollbarWidth;
        return Math.max(0, availableWidth - 1);
    }

    private updatePinnedBottomRows(): void {
        this.internalPinnedBottomRows = this._pinnedBottomRows ?? [];
    }

    /*
    private string getCellStyleClass(node: IRowNode, column: TableColumn): string {
        if (node.data.values[column.id] instanceof TableCell && node.data.values[col.id].styleClass) {

        }
        } ? params.data.values[col.id].styleClass : col.getStyleClassCallback != null && params.node.rowPinned != 'bottom' ? 
                        col.getStyleClassCallback(TableCell.getValue(params.data.values[col.id]), params.node.rowIndex, this.getColIndex(col.id)) : null
    }

  */

    getFilterParams(col: TableColumn): any {
        let simpleFilterParams: any = {
            suppressExcelMode: true,
            filterInputClass: "p-inputtext",
            trimInput: true,
        };

        if (col.cellType == TableCellType.string) {
            simpleFilterParams.filterOptions = [
                "contains",
                "notContains",
                "equals",
                "notEqual",
                "startsWith",
                "endsWith",
                "blank",
                "notBlank",
                {
                    displayKey: "regex",
                    displayName: "Regex",
                    numberOfInputs: 1,
                    predicate: ([filterValue], cellValue) =>
                        this.matchesRegexFilter(filterValue, cellValue),
                },
            ];
        }

        if (col.cellType == TableCellType.dateTime) {
            simpleFilterParams.comparator = (filterDate: Date, cellValue: unknown): number =>
                Dates.withoutTime(Dates.to(cellValue)).getTime() - filterDate.getTime();
            simpleFilterParams.inRangeInclusive = true;
        }

        if (!this.isEnterprise) {
            return simpleFilterParams;
        }

        return {
            filters: [
                {
                    filter: this.getSimpleFilterType(col),
                    filterParams: simpleFilterParams,
                },
                {
                    filter: "agSetColumnFilter",
                    display: "subMenu",
                },
            ],
        };
    }

    private getSimpleFilterType(col: TableColumn): string {
        switch (col.cellType) {
            case TableCellType.int:
            case TableCellType.decimal:
            case TableCellType.byteSize:
            case TableCellType.duration:
            case TableCellType.durationNanos:
                return "agNumberColumnFilter";
            case TableCellType.date:
            case TableCellType.dateTime:
                return "agDateColumnFilter";
            default:
                return "agTextColumnFilter";
        }
    }

    matchesRegexFilter(filterValue: any, cellValue: any): boolean {
        if (Utils.isNoe(filterValue)) {
            return true;
        }

        try {
            return new RegExp(Objects.toString(filterValue), "i").test(Objects.toString(TableCell.getValue(cellValue)));
        }
        catch {
            return false;
        }
    }

    private isCellEditableInternal(
        rowNode: IRowNode,
        column: TableColumn,
    ): boolean {
        if (this._readOnly || rowNode.rowPinned == "bottom" || rowNode.data == null) {
            return false;
        }
        let col = this.getColDefAndIndex(column.id);
        return column.isEditable(
            new TableCellEvent(
                TableCell.getValue(rowNode.data.values[column.id]),
                rowNode.data as TableRow,
                col.def,
                rowNode.rowIndex,
                col.index,
            ),
        );
    }

    private getCellColSpan(params: ColSpanParams, col: TableColumn): number {
        if (params.data == null) {
            return 1;
        }
        let v = params.data.values[col.id];
        if (v instanceof TableCell) {
            return v.colSpan == -1
                ? this._data.columns.length +
                      (this._allowRemoveRow ? 1 : 0) -
                      this.getColIndex(col.id)
                : v.colSpan;
        }
        return 1;
    }

    private getCellRenderer(
        params: Pick<ICellRendererParams, "node" | "data" | "colDef" | "value">,
        column: TableColumn,
    ): CellRendererSelectorResult {
        if (params.node.group && (this.treeGroupColumnId == null || params.data == null)) {
            return null;
        }
        if (params.colDef?.cellDataType == "boolean" && params.value == null) {
            return { component: () => "" };
        }
        if (this.overrideUseTableCellRenderer === true) {
            return { component: SmTableCellComponent };
        }
        if (this.overrideUseTableCellRenderer === false) {
            return null;
        }
        if (column.cellType == TableCellType.button ||
            column.cellType == TableCellType.color ||
            column.cellType == TableCellType.link) {
            return { component: SmTableCellComponent };
        }
        let tableCell = Objects.castOrNull(params.data.values[column.id], TableCell);
        if (column.cellType != TableCellType.bool &&
            (this.actionButtons && !this.actionButtonsOnRightClick ||
                !Utils.isNoe(column.actionProviders) ||
                !Utils.isNoe(tableCell?.actionProviders) ||
                !Utils.isNoe(tableCell?.addOnButtons) ||
                tableCell?.toolTip != null ||
                column.toolTipGetter != null ||
                params.data.toolTip != null)) {
            return { component: SmTableCellComponent };
        }
        if (params.data.values[column.id] instanceof TableCell &&
            params.data.values[column.id].keepHtml) {
            //HTML beibehalten
            return { component: SmTableCellComponent };
        }
        if (column.keepHtml) {
            return { component:p => p.value };
        }
        return null;
    }

    private getCellEditor(
        params: ICellEditorParams,
        column: TableColumn,
    ): CellEditorSelectorResult {
        if (!column.editable || params.node.rowPinned == "bottom") {
            return null;
        }
        if (column.editor != null) {
            let editor = column.getEditor(
                () =>
                    new TableCellEvent(
                        TableCell.getValue(params.data.values[column.id]),
                        params.data as TableRow,
                        this.colDefs.find(colDef => colDef.colId == column.id),
                        params.node.rowIndex,
                        this.getColIndex(column.id),
                    ),
            );
            if (editor == null) {
                return null;
            }
            return {
                component: SmTableCellEditorComponent,
                params: {
                    editor,
                },
            };
        }
        if (column.cellType == TableCellType.string) {
            return {
                component: "agTextCellEditor",
                params: {
                    maxLength: column.editableMaxLength,
                },
            };
        }
        if (column.cellType == TableCellType.int ||
            column.cellType == TableCellType.decimal) {
            return {
                component: "agNumberCellEditor",
                params: {
                    precision: column.numDecimalDigits,
                    max:
                        column.editableMaxLength != null
                            ? 10 ** column.editableMaxLength - 1
                            : null,
                },
            };
        }
        if (column.cellType == TableCellType.date) {
            return {
                component: "agDateCellEditor",
                params: {},
            };
        }
        return null;
    }

    @Input()
    set selection(value: any) {
        this._selection = value;
    }

    doSizeColumnsToFit = false;
    contentFitFillColumnWidthsScheduled = false;
    contentFitFillColumnWidthsApplied = false;

    selectFirstRow(): void {
        this.grid.api.getDisplayedRowAtIndex(0)?.setSelected(true);
    }

    ngDoCheck(_): void {
        if (this.grid != null && this.mustRefresh?.()) {
            this.refresh();
        }

        if (this.fullRowEditForm != null) {
            for (let field of this.fullRowEditForm.fields) {
                this.columnToField(field, null);
            }
        }
        this.grid?.api?.setGridOption("headerHeight", this.headerHeight);

        if (this.doSizeColumnsToFit) {
            if (this.horizontalScroll) {
                this.doSizeColumnsToFit = false;
            }
            else {
                if (this.app.advancedDeveloper) {
                    console.log(
                        "advancedDeveloper: sizeColumnsToFit " +
                            Arrays.itemsToString(this._data?.columns, ", ", c => c.caption),
                    );
                }
                this.doSizeColumnsToFit = !this.sizeColumnsToFit();
            }
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    isFullScreenRowEditorColumnVisible(): boolean {
        return this._fullScreenRowEditor && this.app.isMobile();
    }

    async editRowFullScreen(rowNode: IRowNode): Promise<void> {
        this.fullRowEditForm = new FrontendForm(
            this.colDefs
                .filter(colDef =>
                        colDef.colId !=
                            SmTableComponent.COLUMN_ID_EDIT_ROW_FULL_SCREEN &&
                        colDef.colId != SmTableComponent.COLUMN_ID_REMOVE,
                )
                .map(colDef => this.columnToField(null, colDef)),
        );
        this.fullRowEditRowNode = rowNode;

        try {
            await this.app.dialog.form(this.fullRowEditForm, "Zeile editieren", this.app.dialog.ok, this.app.isMobile());
        }
        finally {
            this.fullRowEditForm = null;
            this.fullRowEditRowNode = null;
        }
    }

    private getTableColumn(id: string): TableColumn {
        return this._data.columns.find(col2 => col2.id == id);
    }

    private columnToField(field: FrontendField, colDef: ColDef): FrontendField {
        if (colDef == null) {
            colDef = this.colDefs.find(colDef2 => colDef2.colId == field.id);
        }
        let col = this.getTableColumn(colDef.colId);
        if (field == null) {
            field = new FrontendField(
                colDef.colId,
                colDef.headerName,
                TableCellType.toFrontendFieldType((colDef.context.tableColumn as TableColumn).cellType),
                {
                    onValueChanged: () => {
                        const params = {
                            data: this.fullRowEditRowNode.data,
                            newValue: field.value,
                            oldValue:
                                this.fullRowEditRowNode.data[colDef.colId],
                            colDef,
                            //  column: column,
                            node: this.fullRowEditRowNode,
                            api: this.grid.api,
                            //  columnApi: this.gridColumnApi,
                            context: this.grid.context,
                        };
                        (colDef.valueSetter as any)(params);

                        this.cellValueChanged({
                            data: this.fullRowEditRowNode.data,
                            colDef,
                            node: this.fullRowEditRowNode,
                        });
                    },
                },
            );
            field.maxValue =
                col.cellType == TableCellType.int &&
                col.editableMaxLength != null
                    ? 10 ** col.editableMaxLength - 1
                    : null;
        }

        if (this.fullRowEditRowNode != null) {
            if (field.value == null) {
                field.value = this.grid.api.getCellValue({
                    rowNode: this.fullRowEditRowNode,
                    colKey: colDef.colId,
                });
            }
            field.enabled = this.isCellEditableInternal(this.fullRowEditRowNode, colDef.context.tableColumn as TableColumn);
            let editor = col.getEditor(
                () =>
                    new TableCellEvent(
                        null,
                        this.fullRowEditRowNode?.data as TableRow,
                        colDef,
                        this.fullRowEditRowNode?.rowIndex ?? -1,
                        this.getColIndex(colDef.colId),
                    ),
            );
            let isDropdown = editor?.type == FrontendFieldType.dropdown;
            field.listItems = isDropdown ? editor.listItems : null;
            field.type = isDropdown ? FrontendFieldType.dropdown : field.type;
        }
        return field;
    }

    isCellEditable(rowIndex: number, col: Column): boolean {
        const colDef = col.getColDef();

        if (colDef.editable === true) {
            return true;
        }

        if (typeof colDef.editable === "function") {
            const rowNode = this.grid.api.getDisplayedRowAtIndex(rowIndex);
            if (!rowNode) {
                return false;
            }

            const params = {
                node: rowNode,
                data: rowNode.data,
                rowIndex,
                colDef,
                column: col,
                api: this.grid.api,
                context: this.context,
            };

            return colDef.editable(params);
        }

        return false;
    }

    getFirstEditableColId(rowIndex: number): string | null {
        const columns = this.grid.api.getAllDisplayedColumns();

        for (let col of columns) {
            const colDef = col.getColDef();

            const isNavigable = !colDef.suppressNavigable;
            const isEditable = this.isCellEditable(rowIndex, col);

            if (isNavigable && isEditable) {
                return col.getColId();
            }
        }

        return null;
    }

    getLastEditableColId(rowIndex: number): string | null {
        const columns = this.grid.api.getAllDisplayedColumns();

        for (let i = columns.length - 1; i >= 0; i--) {
            const col = columns[i];
            const colDef = col.getColDef();

            const isNavigable = !colDef.suppressNavigable;
            const isEditable = this.isCellEditable(rowIndex, col);

            if (isNavigable && isEditable) {
                return col.getColId();
            }
        }

        return null;
    }

    isCompact(): boolean {
        return this.compact ?? this.app.compactGui;
    }

    getTheme(): string {
        return (this.isCompact() ? "balham" : "quartz") +
            (this.app.isDarkMode() ? "-dark" : "");
    }

    getRowHeight(event: RowHeightParams): number {
        // Automatisch erzeugte Ordner- und Gruppenzeilen haben keinen eigenen Datensatz.
        return (event.data as TableRow)?.height ?? null;
    }

    cellKeyDown(event: CellKeyDownEvent | FullWidthCellKeyDownEvent): void {
        let e = event as CellKeyDownEvent;
        let key = (e.event as any).key as string;
        if (this.addRowOnTabInLastCell && key === "Tab") {
            const lastRowIndex = this.grid.api.getDisplayedRowCount() - 1;

            if (event.rowIndex === lastRowIndex &&
                e.column.getColId() == this.getLastEditableColId(e.rowIndex)) {
                event.event.preventDefault();
                this.addRow();
                //Angular
                GuiUtils.angularTimerDefer2(() => {
                    this.grid.api.startEditingCell({
                        rowIndex: lastRowIndex + 1,
                        colKey: this.getFirstEditableColId(lastRowIndex),
                    });
                });
            }
        }
        //Übernommen aus GradeEditor. So würde es funktioniert. Braucht aber noch Feinschliff
        /*   else if (key == "ArrowDown") {
            this.grid.api.stopEditing();
            this.grid.api.tabToNextCell();
            this.grid.api.startEditingCell({
                rowIndex: this.grid.api.getFocusedCell().rowIndex,
                colKey: this.grid.api.getFocusedCell().column.getColId()
            });                
        }
        else if (key == "ArrowUp") {
            this.grid.api.stopEditing();
            this.grid.api.tabToPreviousCell();
            this.grid.api.startEditingCell({
                rowIndex: this.grid.api.getFocusedCell().rowIndex,
                colKey: this.grid.api.getFocusedCell().column.getColId()
            });
        }*/
    }

    cellClicked(event: CellClickedEvent): void {
        let mouseEvent = event.event as MouseEvent;
        if (this.replaceSingleSelectionOnRowClick &&
            this._checkBoxSelection &&
            this.multiSelection &&
            event.column.getColId() != SELECTION_COLUMN_ID &&
            !mouseEvent?.ctrlKey &&
            !mouseEvent?.shiftKey &&
            !mouseEvent?.metaKey &&
            this.grid.api.getSelectedNodes().length <= 1) {
            event.node.setSelected(true, true);
        }

        let cell = Objects.castOrNull(event.data.values[event.colDef.colId], TableCell);
        let toolTipComponent = cell?.toolTip?.getComponentOnClick();
        if (toolTipComponent != null) {
            //TODO
        }

        if (this.editOnClick &&
            this.grid.api.getCellEditorInstances({
                rowNodes: [event.node],
                columns: [event.column],
            }).length == 0) {
            this.grid.api.startEditingCell({
                rowIndex: event.rowIndex,
                colKey: event.column.getId(),
            });
        }
    }

    cellEditingStarted(event: CellEditingStartedEvent): void {
        if (this.onCellEditingStarted.observed) {
            let row = event.data as TableRow;
            this.onCellEditingStarted.emit(
                new TableCellEvent(
                    TableCell.getValue(row.values[event.colDef.colId]),
                    row,
                    event.colDef,
                    event.node.rowIndex,
                    this.getColIndex(event.colDef.colId),
                ),
            );
        }
    }

    cellEditingStopped(event: CellEditingStoppedEvent): void {
        if (this.onCellEditingStopped.observed) {
            let row = event.data as TableRow;
            //CheckboxEditor hat hier eine Besonderheit. Wenn man vor dem Editieren in die Zelle klickt, damit der Editor startet, klappt alles,
            //aber wenn man ohne dies zu tun direkt die Checkbox anklickt, steht in event.newValue nichts drinnen, deshalb dieser Workaround
            let col = this.getTableColumn(event.colDef.colId);
            let value = event.newValue;
            if (col.cellType == TableCellType.bool && value == null) {
                value = row.values[col.id];
            }
            this.onCellEditingStopped.emit(
                new TableCellEvent(
                    value,
                    row,
                    event.colDef,
                    event.node.rowIndex,
                    this.getColIndex(event.colDef.colId),
                ),
            );
        }
    }

    getCellObjectActions(
        params: Pick<ICellRendererParams, "data" | "value" | "colDef">,
    ): ObjectActionWithObject[] {
        const tableColumn = params.colDef?.context?.tableColumn as TableColumn;
        const value = tableColumn?.cellType == TableCellType.date || tableColumn?.cellType == TableCellType.dateTime
            ? tableColumn.toCsvString(params.value)
            : Objects.toString(params.value);
        return [
            new ObjectActionWithObject(
                {
                    getCaption: () => "In Zwischenablage kopieren",
                    getIcon: () => "fas fa-clipboard",
                    isUsable:item => Strings.is(item),
                    perform: async (item) => {
                        await this.app.copyToClipboardOrDialog(item as string, "Zellentext", "Text in Zwischenablage kopiert");
                        return null;
                    },
                },
                value,
            ),
            new ObjectActionWithObject(
                {
                    getCaption: () => "Text in Fenster öffnen",
                    getIcon: () => "fas fa-window-maximize",
                    isUsable:item => Strings.is(item),
                    perform:item => {
                        this.app.dialog.textArea("Zellentext", item as string, null, true);
                        return null;
                    },
                },
                value,
            ),
            new ObjectActionWithObject(
                {
                    getCaption: () => "Texte dieser Spalte in Fenster öffnen",
                    getIcon: () => "fas fa-window-maximize",
                    isUsable:_ => true,
                    perform:_ => {
                        let texts = this.getUnfilteredRows().map(row => Objects.toString(TableCell.getValue(row.values[params.colDef.colId])).replace(/(?:\r\n|\r|\n)+$/, "")).filter(text => !Utils.isNoe(text));
                        this.app.dialog.textArea("Spaltenwerte", Arrays.itemsToString(texts, "\n"), null, true);
                        return null;
                    },
                },
                value,
            ),
            new ObjectActionWithObject(
                {
                    getCaption: () => "Texte dieser Zeile in Fenster öffnen",
                    getIcon: () => "fas fa-window-maximize",
                    isUsable:_ => params.data != null,
                    perform:_ => {
                        const row = params.data as TableRow;
                        const displayedColumns = this.grid.api.getAllDisplayedColumns();
                        const columns = displayedColumns.map(column => this.getTableColumn(column.getColId())).filter(column => column != null && column.cellType != TableCellType.button);
                        const values = columns.map(column => column.toCsvString(row.values[column.id] ?? ""));
                        const csvLine = values.map(text => this._data.toCsvCell(text)).join(";");
                        const namedValues = columns.map((column, index) =>
                            (column.getFullCaption() || column.id) + ": " + values[index].replace(/\r/g, "\\r").replace(/\n/g, "\\n"),
                        );
                        this.app.dialog.textArea("Zeilenwerte", csvLine + "\n\n" + namedValues.join("\n"), null, true);
                        return null;
                    },
                },
                value,
            ),
            new ObjectActionWithObject(
                {
                    getCaption: () => "Nur Zeilen mit diesem Wert anzeigen",
                    getIcon: () => "fas fa-filter",
                    isUsable:_ => this.allowFilter,
                    perform:item => {
                        this.setFilter(params.colDef.colId, item as string);
                        return null;
                    },
                },
                value,
            ),
            ...ObjectActionRegistry.getGlobalActions(value),
        ];
    }

    getCellSemanticObjectActions(params: Pick<ICellRendererParams, "data" | "colDef">): ObjectActionWithObject[] {
        const row = params.data as TableRow;
        const column = params.colDef.context.tableColumn as TableColumn;
        const tableCell = Objects.castOrNull(row?.values[column.id], TableCell);
        const providers = [...column.actionProviders ?? [], ...tableCell?.actionProviders ?? []];
        return ObjectActionRegistry.getSemanticActions(this.getCellGenericObjectKeys(row, column, tableCell), providers);
    }

    getCellSemanticObjectToolTip(params: Pick<ICellRendererParams, "data" | "colDef">): ToolTipDefinition {
        const row = params.data as TableRow;
        const column = params.colDef.context.tableColumn as TableColumn;
        const tableCell = Objects.castOrNull(row?.values[column.id], TableCell);
        const providers = [...column.actionProviders ?? [], ...tableCell?.actionProviders ?? []];
        for (let key of this.getCellGenericObjectKeys(row, column, tableCell)) {
            for (let provider of providers) {
                let toolTip = provider.getToolTip?.(key);
                if (toolTip != null) {
                    return toolTip;
                }
            }
        }
        return null;
    }

    private getCellGenericObjectKeys(row: TableRow, column: TableColumn, tableCell: TableCell): any[] {
        if (!Utils.isNoe(tableCell?.internal)) {
            return tableCell.internal;
        }
        return column.genericObjectKeys?.(TableCell.getValue(row?.values[column.id]), row) ?? [];
    }

    getCellNonSemanticObjectActions(params: Pick<ICellRendererParams, "data" | "value" | "colDef">, semanticActions = this.getCellSemanticObjectActions(params)): ObjectActionWithObject[] {
        const semanticActionDefinitions = new Set(semanticActions.map(action => action.action));
        return this.getCellObjectActions(params).filter(action => !semanticActionDefinitions.has(action.action));
    }

    cellContextMenu(event: CellContextMenuEvent): void {
        if (this.ignoreCellContextMenu) {
            return;
        }

        event.event.stopPropagation();
        event.event.preventDefault();

        const row = event.data as TableRow;
        const col = event.colDef;
        const column = this.getTableColumn(col.field == "values.outline" ? "outline" : col.colId);
        if (column == null) {
            return;
        }

        const callback = column.getContextMenuItems ?? row.getContextMenuItems;
        const semanticActions = this.actionButtonsOnRightClick ? this.getCellSemanticObjectActions(event) : [];
        const possibleActions = this.actionButtons && this.actionButtonsOnRightClick
            ? this.getCellNonSemanticObjectActions(event, semanticActions).filter(action => action.action.isUsable(action.on))
            : [];
        const menuItems = [
            ...ObjectActionRegistry.getSemanticActionMenuItems(semanticActions),
            ...semanticActions.length > 0 && possibleActions.length > 0 ? [{ separator: true }] : [],
            ...possibleActions.map(action => ({
                label: action.getCaption(),
                icon: action.getIcon(),
                command: () => action.perform(),
            })),
            ...callback != null
                ? callback(
                      new TableCellEvent(
                          event.value,
                          row,
                          col,
                          event.node.rowIndex,
                          this.getColIndex(col.colId),
                      ),
                  )
                : [],
        ];
        if (!Utils.isNoe(menuItems)) {
            this.app.openContextMenu(menuItems);
        }
    }

    ignoreCellContextMenu = false;

    gridContextMenu(event: PointerEvent): void {
        if (event.getModifierState("Control")) {
            this.ignoreCellContextMenu = true;
            Utils.setTimerOnce(100, () => {
                this.ignoreCellContextMenu = false;
            });
            return;
        }
        event.stopPropagation();
        event.preventDefault();
    }

    cellFocused(_: CellFocusedEvent): void {
        if (this.onCellFocused.observed) {
            /*     let row = event.data as TableRow;
            let col = event.colDef as ColDef;
            this.onCellFocused.emit(new TableCellEvent(TableCell.getValue(row.values[col.colId]), row, col, event.rowIndex, this.getColIndex(col.colId)));         
       */
        }
    }

    getRowClass(params: any): string {
        return (params.data as TableRow)?.styleClass;
    }

    rowDoubleClicked(event: any): void {
        this.onRowDoubleClicked.emit(event.data as TableRow);
    }

    rowSelected(event: any): void {
        if (event.node.isSelected()) {
            this.onRowSelect.emit(event.data as TableRow);
        }
    }

    rowDragEnd(event: RowDragEndEvent): void {
        if (this.rowDragManaged) {
            const newOrder: TableRow[] = [];
            this.grid.api.forEachNode(node => {
                newOrder.push(node.data as TableRow);
            });
            this._data.rows = newOrder;
            if (this.performSetTableRows != null) {
                this.performSetTableRows(newOrder);
            }
        }
        else {
            if (event.overNode == null || this.dropDraggedRow == null) {
                return;
            }
            this.dropDraggedRow(event.node.data as TableRow, event.overNode.data as TableRow, false);
        }
    }

    rowDragMove(event: RowDragMoveEvent): void {
        if (event.overNode == null) {
            return;
        }
        let result =
            this.dropDraggedRow != null
                ? this.dropDraggedRow(event.node.data as TableRow, event.overNode.data as TableRow, true)
                : null;
        if (result?.dropText != null) {
            const elHint = document.querySelector(".ag-dnd-ghost");
            const elLabel = document.querySelector(".ag-dnd-ghost-label");
            if (elHint == null || elLabel == null) {
                return;
            }

            (elLabel as any).innerText = result.dropText;
            for (let clazz of Array.from(elHint.classList)) {
                if (!clazz.startsWith("ag-")) {
                    elHint.classList.remove(clazz);
                }
            }
            for (let clazz of result.styleClasses ?? []) {
                elHint.classList.add(clazz);
            }
        }
    }

    public getRowId: GetRowIdFunc = (params: GetRowIdParams) =>
        (params.data as TableRow).id;

    selectionChanged(event: any): void {
        this._selection = event.api.getSelectedRows().map(r => r as TableRow);
        this.selectionChange.emit(this._selection);
    }

    setSelectedRowIndex(index: number): void {
        this.grid.api.getDisplayedRowAtIndex(index)?.setSelected(true);
    }

    getColIndex(id: string): number {
        return this.colDefs.findIndex(colDef => colDef.colId == id);
    }

    getColDefAndIndex(id: string): { def: ColDef; index: number } {
        let i = 0;
        for (let def of this.colDefs) {
            if (def.colId == id) {
                return { def, index: i };
            }
            for (let def2 of (def as ColGroupDef).children ?? []) {
                if ((def2 as ColDef).colId == id) {
                    return { def: def2 as ColDef, index: i };
                }
                i++;
            }
            i++;
        }
        return null;
    }

    cellValueChanged(event: Partial<CellValueChangedEvent>): void {
        let row = event.data as TableRow;
        let col = event.colDef;
        this.onCellValueChanged.emit(
            new TableCellEvent(
                TableCell.getValue(row.values[col.colId]),
                row,
                col,
                event.node.rowIndex,
                this.getColIndex(col.colId),
            ),
        );
        if (!this.settingBooleanHeaderCheckbox &&
            (col.context?.tableColumn as TableColumn)?.headerCheckbox) {
            this.grid?.api?.refreshHeader();
        }
    }

    sortChanged(event: SortChangedEvent): void {
        this.sortCache.clear();
        if (!this.applyingTableStatus) {
            this.onSortChanged?.emit(event);
        }
    }

    filterChanged(event: FilterChangedEvent): void {
        if (!this.applyingTableStatus) {
            this.onFilterChanged?.emit(event);
        }
        if (this._data?.columns.some(col => col.headerCheckbox)) {
            event.api.refreshHeader();
        }
        if (this.usesContentWidthsForAllColumns()) {
            this.scheduleContentColumnWidths();
        }
    }

    modelUpdated(event: ModelUpdatedEvent): void {
        if (this._data?.columns.some(col => col.headerCheckbox)) {
            event.api.refreshHeader();
        }
        if (this.usesContentWidthsForAllColumns()) {
            this.scheduleContentColumnWidths();
        }
    }

    getUnfilteredRows(): TableRow[] {
        let result: TableRow[] = [];
        this.grid.api.forEachNodeAfterFilterAndSort(node => {
            result.push(node.data as TableRow);
        });

        return result;
    }

    getBooleanHeaderCheckboxState(column: TableColumn, api: GridApi = this.grid?.api): { value: boolean; indeterminate: boolean; disabled: boolean } {
        let count = 0;
        let selected = 0;
        api?.forEachNodeAfterFilterAndSort(node => {
            let row = node.data as TableRow;
            if (row?.virtual || !this.isCellEditableInternal(node, column)) {
                return;
            }
            count++;
            if (Utils.toBool(TableCell.getValue(row.values[column.id]))) {
                selected++;
            }
        });
        return {
            value: count > 0 && selected == count,
            indeterminate: selected > 0 && selected < count,
            disabled: count == 0,
        };
    }

    setBooleanHeaderCheckboxValue(column: TableColumn, value: boolean, api: GridApi = this.grid?.api): void {
        if (api == null) {
            return;
        }
        this.settingBooleanHeaderCheckbox = true;
        try {
            api.forEachNodeAfterFilterAndSort(node => {
                let row = node.data as TableRow;
                if (row?.virtual || !this.isCellEditableInternal(node, column) ||
                    Utils.toBool(TableCell.getValue(row.values[column.id])) == value) {
                    return;
                }
                node.setDataValue(column.id, value);
            });
        }
        finally {
            this.settingBooleanHeaderCheckbox = false;
        }
        api.refreshCells({ columns: [column.id], force: true });
        api.refreshHeader();
    }

    getCsvColumns(): TableColumn[] {
        return this._data.columns.filter(col => col.cellType != TableCellType.button);
    }

    async configureColumns(): Promise<void> {
        let columns = this._data.columns.filter(col => this.grid.api.getColumn(col.id) != null);
        let columnState = new Map(this.grid.api.getColumnState().map(col => [col.colId, col]));
        let selectedColumnIds = await this.app.dialog.input<string[]>(
            new FrontendField("columns", this.app.t("table.columns"), FrontendFieldType.listBox, {
                value: columns.filter(col => columnState.get(col.id)?.hide != true).map(col => col.id),
                listItems: columns.map(col => new FrontendFieldListItem(col.id, col.getFullCaption() || col.id)),
                listBoxCheckBoxes: true,
                dropdownCanFilter: true,
                fixedHeight: "60vh",
                mandatory: true,
            }),
            this.app.t("table.configureColumns"),
        );
        if (selectedColumnIds == null) {
            return;
        }

        let selectedColumns = new Set(selectedColumnIds);
        this.grid.api.applyColumnState({
            state: columns.map(col => ({
                colId: col.id,
                hide: !selectedColumns.has(col.id),
            })),
            applyOrder: false,
        });
        this.onTableStatusChanged.emit(this.getTableStatus());
        if (this.usesContentWidthsForAllColumns()) {
            this.scheduleContentColumnWidths();
        }
        else if (!this.horizontalScroll) {
            GuiUtils.angularTimer(() => this.sizeColumnsToFit());
        }
    }

    async configureTableExpressionFilter(): Promise<void> {
        let columns = this._data.columns.filter(col => col.cellType != TableCellType.button);
        let form = new FrontendForm([
            new FrontendField("columns", "Verfügbare Spalten", FrontendFieldType.table, {
                value: columns.map(col => ({
                    id: col.id,
                    caption: col.getFullCaption() || col.id,
                })),
                guiGroup: "tableExpressionFilter",
                guiWidthInGroup: 45,
                fixedHeight: "180px",
                tableCanAddRow: false,
                tableCanRemoveRow: false,
                tableCompact: true,
                tableColumns: [
                    new TableColumn("id", "Interner Name", TableCellType.string, 1),
                    new TableColumn("caption", "Anzeigename", TableCellType.string, 1),
                ],
            }),
            new FrontendField("expression", "Scriban-/DHC-Ausdruck", FrontendFieldType.textArea, {
                value: this.tableExpressionFilterText,
                guiGroup: "tableExpressionFilter",
                guiWidthInGroup: 55,
                fixedHeight: "180px",
                monospaced: true,
                placeholder: "<<Status == 'Offen' && Betrag > 100>>",
                tooltip: "Der Ausdruck wird für jede Zeile ausgewertet und muss true oder false zurückgeben. Die Spalten stehen direkt über ihren internen Namen zur Verfügung; über _row['Spalten-ID'] sind auch IDs mit Leerzeichen oder Sonderzeichen erreichbar.",
            }),
        ]);
        if (await this.app.dialog.form(form, "Tabellenzeilen filtern") != "ok") {
            return;
        }
        let expression = form.getValueString("expression");
        if (Utils.isNoe(expression)) {
            this.clearTableExpressionFilter();
            return;
        }

        let rows = this._data.rows.map(row => {
            let result: OrdinaryObject = {};
            for (let col of this._data.columns) {
                if (col.cellType != TableCellType.button) {
                    result[col.id] = TableCell.getComparable(row.values[col.id]);
                }
            }
            return result;
        });
        let evaluation = await this.app.genericDataHandler(async () => RestEndpoint.main().post().body({ expression, rows }).run("api/tools/filterTableRows").listNumbers(), "Tabellenzeilen konnten nicht gefiltert werden", "dialog");
        if (!evaluation.ok) {
            return;
        }

        this.tableExpressionFilterText = expression;
        this.tableExpressionFilterRows = new Set(evaluation.value.map(index => this._data.rows[index]));
        this.grid.api.onFilterChanged();
    }

    clearTableExpressionFilter(): void {
        this.tableExpressionFilterText = "";
        this.tableExpressionFilterRows = null;
        this.grid.api.onFilterChanged();
    }

    exportCsv(columns: TableColumn[] = null): void {
        GuiUtils.downloadBytesAsFile(GuiUtils.stringAsciiToArrayBuffer(this._data.toCsv(this.getUnfilteredRows(), columns)), "Tabelle.csv");
    }

    async exportCsvToClipboard(columns: TableColumn[] = null): Promise<void> {
        await this.app.copyToClipboardOrDialog(this._data.toCsv(this.getUnfilteredRows(), columns), "CSV", "CSV in Zwischenablage kopiert");
    }

    async exportCsvWithColumnSelection(toClipboard: boolean): Promise<void> {
        let columns = this.getCsvColumns();
        let selectedColumnIds = await this.app.dialog.input<string[]>(
            new FrontendField("columns", "Spalten", FrontendFieldType.listBox, {
                value: columns.map(col => col.id),
                listItems: columns.map(col =>
                        new FrontendFieldListItem(col.id, col.getFullCaption()),
                ),
                listBoxCheckBoxes: true,
                dropdownCanFilter: true,
                fixedHeight: "60vh",
            }),
            "CSV exportieren",
        );
        if (selectedColumnIds == null) {
            return;
        }
        let selectedColumns = columns.filter(col =>
            selectedColumnIds.includes(col.id),
        );
        if (toClipboard) {
            await this.exportCsvToClipboard(selectedColumns);
        }
        else {
            this.exportCsv(selectedColumns);
        }
    }

    async exportXlsx(): Promise<void> {
        let columns = this.getCsvColumns();
        let csv = this._data.toCsv(this.getUnfilteredRows(), columns);
        let columnState = this.grid.api.getColumnState();
        let xlsx = await GuiUtils.convertCsvToExcel(
            csv,
            columns.map(col =>
                Objects.fromPlain(ExcelColumn, {
                    dataType: TableCellType.toGenericDataType(col.cellType),
                    width:
                        columnState.find(cs => cs.colId == col.id)?.width *
                        0.15,
                }),
            ),
        );
        GuiUtils.downloadBytesAsFile(GuiUtils.base64ToArrayBuffer(xlsx), "Tabelle.xlsx");
    }

    async asciiTableToClipboard(): Promise<void> {
        await this.app.copyToClipboardOrDialog(this._data.toAsciiTable(this.getUnfilteredRows()), "ASCII-Tabelle", "ASCII-Tabelle in Zwischenablage kopiert");
    }

    toggleFilterRow(): void {
        if (!this.allowFilter) {
            return;
        }
        this.floatingFilter = !this._floatingFilter;
    }

    setFilter(columnId: string, value: string | OrdinaryObject): void {
        if (!this.allowFilter) {
            return;
        }
        let filterModel = Strings.is(value)
                ? {
                      filterType: "text",
                      type: "contains",
                      filter: value,
                  }
                : (value as OrdinaryObject);
        this.grid.api.setFilterModel({
            [columnId]: this.toCurrentFilterModel(filterModel),
        });
        this.grid.api.onFilterChanged();
    }

    openSettings(event: any): void {
        let unfilteredRows = this.getUnfilteredRows();
        let tableExpressionFilterAvailable = Config.get().activeModules?.includes("DocumentHandlerModule") ?? false;
        this.settingsItems = Arrays.withoutNull([
            {
                label: "Tabelle durchsuchen",
                data: {
                    tableQuickFilter: true,
                },
            },
            this.isEnterprise
                ? {
                      label: this.displaySideBar
                          ? "Sidebar verstecken"
                          : "Sidebar anzeigen",
                      command: () => {
                          this.displaySideBar = !this.displaySideBar;
                      },
                  }
                : null,
            {
                label: this.app.t("table.configureColumns"),
                icon: "fas fa-table-columns",
                command: this.configureColumns.bind(this),
            },
            this.allowFilter
                ? {
                      label: this.app.t("table.toggleFilterRow"),
                      command: this.toggleFilterRow.bind(this),
                  }
                : null,
            tableExpressionFilterAvailable
                ? {
                      label: Utils.isNoe(this.tableExpressionFilterText)
                          ? "Zeilen per Scriban/DHC filtern"
                          : "Scriban-/DHC-Zeilenfilter bearbeiten",
                      icon: "fas fa-code",
                      command: this.configureTableExpressionFilter.bind(this),
                  }
                : null,
            tableExpressionFilterAvailable && !Utils.isNoe(this.tableExpressionFilterText)
                ? {
                      label: "Scriban-/DHC-Zeilenfilter entfernen",
                      icon: "fas fa-filter-circle-xmark",
                      command: this.clearTableExpressionFilter.bind(this),
                  }
                : null,
            {
                label: this.app.t("table.exportCsv"),
                command: () => this.exportCsv(),
                items: [
                    {
                        label: this.app.t("table.exportCsvToFile", null, "Als Datei exportieren", false),
                        command: () => this.exportCsv(),
                    },
                    {
                        label: this.app.t("table.exportCsvToFileSelectColumns", null, "Als Datei exportieren (Spalten auswählen)", false),
                        command: () => this.exportCsvWithColumnSelection(false),
                    },
                    {
                        label: this.app.t("table.exportCsvToClipboard", null, "In Zwischenablage schicken", false),
                        command: () => this.exportCsvToClipboard(),
                    },
                    {
                        label: this.app.t("table.exportCsvToClipboardSelectColumns", null, "In Zwischenablage schicken (Spalten auswählen)", false),
                        command: () => this.exportCsvWithColumnSelection(true),
                    },
                ],
            },
            GuiUtilsInternal.convertCsvToExcelFunction != null
                ? {
                      label: this.app.t("table.exportXlsx"),
                      command: this.exportXlsx.bind(this),
                  }
                : null,
            {
                label: this.app.t("table.asciiTableToClipboard"),
                command: this.asciiTableToClipboard.bind(this),
            },
            this.app.advancedDeveloper
                ? {
                      label: "Debug: TableCellRenderer nutzen",
                      badge:
                          this.overrideUseTableCellRenderer == null
                              ? "DYNAMISCH"
                              : this.overrideUseTableCellRenderer
                                ? "AN"
                                : "AUS",
                      command: () => {
                          this.overrideUseTableCellRenderer =
                              Utils.cycleTriState(this.overrideUseTableCellRenderer);
                      },
                  }
                : null,
            this.app.advancedDeveloper
                ? {
                      label: "Debug: Datenänderungen ignorieren",
                      badge: this.ignoreDataChanges ? "AN" : "AUS",
                      command: () => {
                          this.ignoreDataChanges = !this.ignoreDataChanges;
                      },
                  }
                : null,
            this.app.advancedDeveloper
                ? {
                      label: "Kompakter Modus",
                      badge: this.isCompact() ? "AN" : "AUS",
                      command: () => {
                          this.compact = !this.isCompact();
                      },
                  }
                : null,
            this.app.advancedDeveloper
                ? {
                      label: "Spaltenbreiten neu berechnen",
                      command: () => {
                          this.sizeColumnsToFit();
                      },
                  }
                : null,
            {
                separator: true,
            },
            {
                label: "Anzahl Zeilen: " + this._data.rows.length,
                disabled: true,
            },
            unfilteredRows.length != this._data.rows.length
                ? {
                      label:
                          "Anzahl sichtbarer Zeilen: " + unfilteredRows.length,
                      disabled: true,
                  }
                : null,
        ]);
        this.settingsMenu.toggle(event);
        GuiUtils.angularTimer(() => {
            this.quickFilterInput?.nativeElement.focus();
        });
        event.stopPropagation();
    }

    getSelectedRowCount(): number {
        return this.grid?.api?.getSelectedNodes()?.length ?? 0;
    }

    makeRowVisible(rowId: string): void {
        const rowNode = this.grid.api.getRowNode(rowId);

        if (rowNode == null) {
            return;
        }

        this.grid.api.ensureNodeVisible(rowNode, "middle");
    }

    async addRow(): Promise<void> {
        if (this.performAddTableRow != null) {
            this.performAddTableRow();
        }
        else if (this._data.generator != null) {
            this._data.rows = [
                ...this._data.rows,
                await this._data.generator(),
            ];
        }
        GuiUtils.angularTimer(() =>
            this.grid.api.ensureIndexVisible(this._data.rows.length - 1, "bottom"),
        );
    }

    async removeRow(row: TableRow, showWarningMessage = false): Promise<void> {
        if (showWarningMessage &&
            this.removeRowWarningMessage != null &&
            !await this.app.dialog.yesNo(this.removeRowWarningMessage, this.app.t("general.warning"))) {
            return;
        }
        let index = this._data.rows.findIndex(row2 => row2 == row);
        if (index >= 0) {
            if (this.handleRemove) {
                this._data.rows = Arrays.withoutIndex(this._data.rows, index);
            }
            this.onRowRemoved.emit(new TableRowEvent(row, index));
        }
    }

    tabToNextCell(params: any): boolean | CellPosition {
        let api = params.api;

        let previous = params.previousCellPosition;
        let backwards = params.backwards;

        let rowCount = api.getDisplayedRowCount();

        let nextRowIndex = previous.rowIndex + (backwards ? -1 : 1);

        if (nextRowIndex >= 0 && nextRowIndex < rowCount) {
            return {
                rowIndex: nextRowIndex,
                column: previous.column,
                rowPinned: previous.rowPinned,
            };
        }

        let allColumns = api.getAllDisplayedColumns();
        let currentColIndex = allColumns.findIndex(c => c === previous.column);

        let nextColIndex = currentColIndex + (backwards ? -1 : 1);

        let candidateColumn: AgColumn = allColumns[nextColIndex];

        return {
            rowIndex: backwards ? rowCount - 1 : 0,
            column: candidateColumn,
            rowPinned: null,
        };
    }

    stopCellEditing(): void {
        this.grid.api.stopEditing();
    }

    getGroupColumnIds(): string[] {
        return this.isEnterprise
            ? this.grid.api.getRowGroupColumns().map(c => c.getColId())
            : [];
    }

    addGroupColumn(colId: string): void {
        const currentGroups = this.getGroupColumnIds();

        if (!currentGroups.includes(colId)) {
            const newGroups = [...currentGroups, colId];
            this.grid.api.setRowGroupColumns(newGroups);
        }

        this.grid.api.refreshClientSideRowModel("group");
    }

    removeGroupColumn(colId: string): void {
        const currentGroups = this.getGroupColumnIds();
        const newGroups = currentGroups.filter(id => id !== colId);

        this.grid.api.setRowGroupColumns(newGroups);
        this.grid.api.refreshClientSideRowModel("group");
    }

    setQuickFilterText(text: string): void {
        this.quickFilterText = text ?? "";
        this.grid.api.setGridOption("quickFilterText", this.quickFilterText);
        this.refresh();
    }

    getTableStatus(): SmTableStatus {
        return Objects.fromPlain(SmTableStatus, {
            columnStatus: this.grid.api.getColumnState().map(col =>
                Objects.fromPlain(SmTableColumnStatus, {
                    columnId: col.colId,
                    filter: this.grid.api.getFilterModel()[col.colId],
                    sort: col.sort,
                    sortIndex: col.sortIndex,
                    hide: col.hide,
                    width: col.width,
                    flex: col.flex,
                }),
            ),
        });
    }

    applyTableStatus(status: SmTableStatus): void {
        const currentState = this.grid.api.getColumnState();
        const savedStatus = status.columnStatus ?? [];
        const statusByColId = new Map(
            savedStatus.map(cs => [cs.columnId, cs]),
        );
        const currentStateByColId = new Map(
            currentState.map(col => [col.colId, col]),
        );
        const orderedColumnIds = [
            ...savedStatus.map(cs => cs.columnId).filter(columnId => currentStateByColId.has(columnId)),
            ...currentState.map(col => col.colId).filter(columnId => !statusByColId.has(columnId)),
        ];
        const mergedState: ColumnState[] = orderedColumnIds.map(columnId => {
            const col = currentStateByColId.get(columnId);
            const saved = statusByColId.get(col.colId);
            if (!saved) {
                return {
                    ...col,
                    sort: null,
                    sortIndex: null,
                };
            }

            const result: ColumnState = {
                ...col,
                sort: saved.sort ?? null,
                sortIndex: saved.sortIndex ?? null,
            };
            if ("hide" in saved) {
                result.hide = saved.hide;
            }
            if ("width" in saved) {
                result.width = saved.width;
            }
            if ("flex" in saved) {
                result.flex = saved.flex;
            }
            return result;
        });
        this.applyingTableStatus = true;
        try {
            this.grid.api.applyColumnState({
                state: mergedState,
                applyOrder: true,
            });

            const filterModel: FilterModel = {};

            for (const cs of savedStatus) {
                if (cs.filter != null) {
                    filterModel[cs.columnId] = this.toCurrentFilterModel(cs.filter);
                }
            }

            this.grid.api.setFilterModel(filterModel);

            this.grid.api.onFilterChanged();
        }
        finally {
            this.applyingTableStatus = false;
        }
    }

    columnStatusChanged(event: ColumnMovedEvent | ColumnResizedEvent | ColumnVisibleEvent): void {
        const source = Objects.toString(event.source);
        if (this.applyingTableStatus || "finished" in event && event.finished === false ||
            !["uiColumnDragged", "uiColumnResized", "toolPanelUi", "columnMenu"].includes(source)) {
            return;
        }
        this.onTableStatusChanged.emit(this.getTableStatus());
    }

    private toCurrentFilterModel(filterModel: any): any {
        if (!this.isEnterprise || filterModel == null || filterModel.filterType == "multi") {
            return filterModel;
        }

        return {
            filterType: "multi",
            filterModels: filterModel.filterType == "set" ? [null, filterModel] : [filterModel, null],
        };
    }
}
